import { useEffect, useMemo, useState } from "react";
import {
  Plus, Edit, Trash2, Loader2, Tablet, Laptop, Search, X, Save,
  Sparkles, Wrench, Power, PowerOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = [
  { id: "ipad",    label: "iPad",    icon: Tablet },
  { id: "macbook", label: "MacBook", icon: Laptop },
];

const IPAD_GROUPS = [
  "iPad Pro 13\"", "iPad Pro 12,9\"", "iPad Pro 11\"", "iPad Pro 10,5\"", "iPad Pro 9,7\"",
  "iPad Air 13\"", "iPad Air 11\"", "iPad Air",
  "iPad mini",
  "iPad 10,2\"", "iPad 9,7\"", "iPad",
];
const MACBOOK_GROUPS = [
  "MacBook Pro 16\"", "MacBook Pro 15\"", "MacBook Pro 14\"", "MacBook Pro 13\"",
  "MacBook Air 15\"", "MacBook Air 13\"",
];

export default function AdminRepairCatalog() {
  const [category, setCategory] = useState("ipad");
  const [models, setModels] = useState([]);
  const [repairTypes, setRepairTypes] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  // Dialog state
  const [editingModel, setEditingModel] = useState(null);       // Model to edit prices for
  const [creatingModel, setCreatingModel] = useState(false);    // New model dialog

  const load = async () => {
    setLoading(true);
    try {
      const [m, t] = await Promise.all([
        api.get("/admin/repair-catalog/models", { params: { category } }),
        api.get("/repair-catalog/repair-types"),
      ]);
      setModels(m.data || []);
      setRepairTypes(t.data.types || []);
    } catch (e) {
      toast.error("Konnte Katalog nicht laden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); setSelectedGroup(null); }, [category]); // eslint-disable-line

  const groups = category === "ipad" ? IPAD_GROUPS : MACBOOK_GROUPS;
  const groupCount = useMemo(() => {
    const c = {};
    for (const m of models) c[m.group] = (c[m.group] || 0) + 1;
    return c;
  }, [models]);

  const filtered = useMemo(() => {
    let list = models;
    if (selectedGroup) list = list.filter((m) => m.group === selectedGroup);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((m) => m.name.toLowerCase().includes(q) || m.group.toLowerCase().includes(q));
    return list;
  }, [models, selectedGroup, search]);

  const deleteModel = async (m) => {
    if (!confirm(`"${m.name}" und alle Preise löschen?`)) return;
    try {
      await api.delete(`/admin/repair-catalog/models/${m.id}`);
      toast.success("Modell gelöscht");
      await load();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const toggleActive = async (m) => {
    try {
      await api.patch(`/admin/repair-catalog/models/${m.id}`, {
        category: m.category, group: m.group, name: m.name, year: m.year,
        image_url: m.image_url || "", order: m.order || 0, active: !m.active,
      });
      toast.success(m.active ? `"${m.name}" deaktiviert` : `"${m.name}" aktiviert`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status-Wechsel fehlgeschlagen");
    }
  };

  return (
    <div className="text-white space-y-6" data-testid="admin-repair-catalog-page">
      {/* HEADER */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold">Reparatur-Katalog</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">iPad & MacBook</h1>
          <p className="text-sm text-zinc-500 mt-1.5">
            {'Alle Modelle aus '}<span className="text-zinc-300">mcservice.at</span>{' — Reparaturarten sind vorangelegt und stehen auf „Preis auf Anfrage". Klick ein Modell an, um alle Preise auf einmal zu bearbeiten.'}
          </p>
        </div>
        <Button
          onClick={() => setCreatingModel(true)}
          className="bg-[#DC1928] hover:bg-[#B01829] text-white rounded-full h-11 px-5 shrink-0"
          data-testid="catalog-add-model"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Neues Modell
        </Button>
      </div>

      {/* CATEGORY TABS */}
      <div className="flex gap-2 flex-wrap">
        {CATEGORIES.map((c) => {
          const Icon = c.icon;
          const active = category === c.id;
          const count = active ? models.length : 0;
          return (
            <button
              key={c.id}
              onClick={() => setCategory(c.id)}
              className={`inline-flex items-center gap-1.5 h-10 px-4 rounded-full transition-all border ${
                active
                  ? "bg-[#DC1928] border-[#DC1928] text-white shadow-[0_4px_14px_-4px_rgba(220,25,40,0.6)]"
                  : "bg-[#141417] border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
              }`}
              data-testid={`catalog-tab-${c.id}`}
            >
              <Icon className="w-4 h-4" /> {c.label}
              {active && <span className="text-[10px] rounded-full px-1.5 py-0.5 bg-white/20">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* GROUP CHIPS */}
      <div className="flex flex-wrap gap-1.5">
        <button
          onClick={() => setSelectedGroup(null)}
          className={`px-3 h-9 rounded-full text-xs font-semibold transition-all border ${
            selectedGroup === null
              ? "bg-white text-[#0A0A0B] border-white"
              : "bg-[#141417] border-white/10 text-zinc-400 hover:text-white"
          }`}
          data-testid="group-chip-all"
        >
          Alle Serien <span className="ml-1 opacity-60">{models.length}</span>
        </button>
        {groups.filter((g) => groupCount[g] > 0).map((g) => (
          <button
            key={g}
            onClick={() => setSelectedGroup(g)}
            className={`px-3 h-9 rounded-full text-xs font-semibold transition-all border ${
              selectedGroup === g
                ? "bg-white text-[#0A0A0B] border-white"
                : "bg-[#141417] border-white/10 text-zinc-400 hover:text-white"
            }`}
            data-testid={`group-chip-${g.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
          >
            {g} <span className="ml-1 opacity-60">{groupCount[g] || 0}</span>
          </button>
        ))}
      </div>

      {/* SEARCH */}
      <div className="relative max-w-md">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Modell suchen…"
          className="pl-10 h-11 bg-[#141417] border-white/10 text-white placeholder:text-zinc-600 focus:border-[#DC1928]"
          data-testid="catalog-search"
        />
      </div>

      {/* MODEL GRID */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-32 rounded-2xl bg-[#141417] border border-white/5 animate-pulse"></div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-14 text-center rounded-2xl border border-dashed border-white/10 bg-[#0F0F11]" data-testid="catalog-empty">
          <p className="text-sm text-zinc-500">Keine Modelle gefunden.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="models-grid">
          {filtered.map((m) => (
            <ModelCard
              key={m.id}
              model={m}
              onEdit={() => setEditingModel(m)}
              onToggle={() => toggleActive(m)}
              onDelete={() => deleteModel(m)}
            />
          ))}
        </div>
      )}

      {/* MODEL PRICE-GRID DIALOG */}
      {editingModel && (
        <ModelPriceDialog
          model={editingModel}
          repairTypes={repairTypes}
          onClose={() => setEditingModel(null)}
          onSaved={async () => { setEditingModel(null); await load(); }}
        />
      )}

      {/* NEW MODEL DIALOG */}
      {creatingModel && (
        <NewModelDialog
          category={category}
          groups={groups}
          onClose={() => setCreatingModel(false)}
          onCreated={async () => { setCreatingModel(false); await load(); }}
        />
      )}
    </div>
  );
}

function ModelCard({ model, onEdit, onToggle, onDelete }) {
  const [count, setCount] = useState(null);
  const [priced, setPriced] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/admin/repair-catalog/models/${model.id}/services`)
      .then((r) => {
        if (!alive) return;
        const list = r.data || [];
        setCount(list.length);
        setPriced(list.filter((s) => !s.on_request && s.price != null).length);
      })
      .catch(() => alive && setCount(0));
    return () => { alive = false; };
  }, [model.id]);

  return (
    <div
      className={`group relative rounded-2xl border p-4 transition-all ${
        model.active
          ? "bg-gradient-to-br from-[#15151A] to-[#101013] border-white/8 hover:border-white/20"
          : "bg-[#0F0F11] border-white/5 opacity-70"
      }`}
      data-testid={`model-card-${model.id}`}
    >
      <button
        type="button"
        onClick={onEdit}
        className="text-left w-full"
        data-testid={`model-card-open-${model.id}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold truncate">{model.group}</div>
            <div className="font-display text-base font-bold text-white mt-0.5 leading-tight">{model.name}</div>
          </div>
          <span className="text-[10px] uppercase tracking-wider text-zinc-500 shrink-0 mt-0.5">{model.year || "—"}</span>
        </div>

        <div className="mt-4 flex items-center gap-3 text-[11px]">
          <span className="inline-flex items-center gap-1 text-zinc-500">
            <Wrench className="w-3 h-3" /> {count == null ? "…" : count} Rep.-Arten
          </span>
          {count != null && priced != null && (
            priced === 0 ? (
              <span className="text-amber-400 font-medium">{'Alle „auf Anfrage"'}</span>
            ) : priced === count ? (
              <span className="text-emerald-400 font-medium">Alle Preise gepflegt</span>
            ) : (
              <span className="text-zinc-400">{priced} von {count} mit Preis</span>
            )
          )}
          {!model.active && <span className="text-red-400 font-medium">Inaktiv</span>}
        </div>
      </button>

      {/* Row actions */}
      <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-1.5">
        <button
          onClick={onEdit}
          className="flex-1 h-8 inline-flex items-center justify-center gap-1 text-[11px] text-zinc-300 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full transition-colors"
          data-testid={`model-edit-btn-${model.id}`}
        >
          <Edit className="w-3.5 h-3.5" /> Preise bearbeiten
        </button>
        <button
          onClick={onToggle}
          className={`w-8 h-8 flex items-center justify-center rounded-full border transition-colors ${
            model.active
              ? "bg-emerald-500/10 border-emerald-500/25 text-emerald-300 hover:bg-emerald-500/20"
              : "bg-zinc-500/10 border-zinc-500/25 text-zinc-400 hover:bg-zinc-500/20"
          }`}
          title={model.active ? "Deaktivieren" : "Aktivieren"}
          data-testid={`model-toggle-${model.id}`}
        >
          {model.active ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />}
        </button>
        <button
          onClick={onDelete}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-red-500/10 hover:bg-red-500/20 border border-red-500/25 text-red-300"
          title="Löschen"
          data-testid={`model-delete-${model.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function ModelPriceDialog({ model, repairTypes, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);   // [{repair_type, price, on_request, duration, description, active, order, id?}]

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/admin/repair-catalog/models/${model.id}/services`);
        if (!alive) return;
        setRows((data || []).map((s) => ({
          id: s.id,
          repair_type: s.repair_type,
          price: s.price == null ? "" : String(s.price),
          on_request: !!s.on_request || s.price == null,
          duration: s.duration || "",
          description: s.description || "",
          active: !!s.active,
          order: s.order || 0,
        })));
      } finally {
        alive && setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [model.id]);

  const update = (idx, patch) => setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const addRow = () => {
    const usedTypes = new Set(rows.map((r) => r.repair_type));
    const nextType = repairTypes.find((t) => !usedTypes.has(t)) || "Sonstige Reparatur";
    setRows((prev) => [...prev, {
      repair_type: nextType, price: "", on_request: true,
      duration: "", description: "", active: true, order: (prev.at(-1)?.order || 0) + 10,
    }]);
  };

  const removeRow = async (idx) => {
    const row = rows[idx];
    if (row.id) {
      if (!confirm(`"${row.repair_type}" wirklich löschen?`)) return;
      try {
        await api.delete(`/admin/repair-catalog/services/${row.id}`);
      } catch (e) {
        return toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen");
      }
    }
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const payload = rows.map((r) => ({
        repair_type: r.repair_type,
        price: r.on_request ? null : (r.price === "" ? null : Number(r.price)),
        on_request: !!r.on_request,
        duration: r.duration,
        description: r.description,
        order: Number(r.order) || 0,
        active: !!r.active,
      }));
      const { data } = await api.post(`/admin/repair-catalog/models/${model.id}/services/bulk`, {
        services: payload,
      });
      toast.success(`Gespeichert · ${data.created} neu, ${data.updated} aktualisiert`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-[#0F0F11] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="model-price-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Wrench className="w-4 h-4 text-[#FF2D40]" />
            <DialogTitle className="font-display text-xl">{model.name}</DialogTitle>
            <span className="text-[10px] uppercase tracking-wider text-zinc-500">{model.group} · {model.year}</span>
          </div>
          <DialogDescription className="text-zinc-500 text-xs">
            {'Preise brutto inkl. 20 % USt. „Auf Anfrage"-Toggle zeigt dem Kunden statt Preis einen WhatsApp-CTA. Leeres Preisfeld = automatisch „auf Anfrage".'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex items-center justify-center text-zinc-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <>
            {/* Sticky action bar — Add button + count */}
            <div className="sticky top-0 z-10 -mx-6 px-6 py-3 bg-[#0F0F11]/95 backdrop-blur border-b border-white/8 flex items-center justify-between mt-2">
              <div className="text-xs text-zinc-400">
                <span className="text-white font-semibold">{rows.length}</span> Reparaturart{rows.length === 1 ? "" : "en"}
              </div>
              <Button
                type="button"
                size="sm"
                onClick={addRow}
                className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-full h-9 px-4"
                data-testid="price-add-row-top"
              >
                <Plus className="w-4 h-4 mr-1.5" /> Reparaturart
              </Button>
            </div>

            <div className="space-y-2 mt-4" data-testid="price-rows">
              {rows.length === 0 ? (
                <div className="py-10 text-center rounded-xl border border-dashed border-white/10 bg-[#0A0A0B]" data-testid="price-empty">
                  <p className="text-sm text-zinc-500">{'Noch keine Reparaturarten. Klick oben „+ Reparaturart".'}</p>
                </div>
              ) : (
                rows.map((r, idx) => (
                  <PriceRow
                    key={r.id || idx}
                    row={r}
                    repairTypes={repairTypes}
                    onChange={(patch) => update(idx, patch)}
                    onRemove={() => removeRow(idx)}
                    testid={`row-${idx}`}
                  />
                ))
              )}
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={addRow}
              className="mt-3 w-full bg-transparent border-dashed border-white/15 text-zinc-300 hover:bg-white/5 hover:text-white rounded-xl h-11"
              data-testid="price-add-row"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Weitere Reparaturart hinzufügen
            </Button>
          </>
        )}

        <DialogFooter className="mt-5">
          <Button
            variant="outline"
            onClick={onClose}
            className="bg-transparent border-white/15 text-white hover:bg-white/5 hover:text-white rounded-full"
            data-testid="price-cancel"
          >
            Abbrechen
          </Button>
          <Button
            onClick={saveAll}
            disabled={saving || loading}
            className="bg-[#DC1928] hover:bg-[#B01829] text-white rounded-full"
            data-testid="price-save-all"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            Alle Preise speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PriceRow({ row, repairTypes, onChange, onRemove, testid }) {
  const [customMode, setCustomMode] = useState(!repairTypes.includes(row.repair_type) && row.repair_type !== "");
  return (
    <div className={`p-3 rounded-xl border ${row.active ? "bg-white/[0.03] border-white/8" : "bg-white/[0.01] border-white/5 opacity-60"}`} data-testid={testid}>
      <div className="grid grid-cols-12 gap-2 items-center">
        {/* Repair type — dropdown OR custom text */}
        {customMode ? (
          <div className="col-span-5 flex gap-1">
            <Input
              value={row.repair_type}
              onChange={(e) => onChange({ repair_type: e.target.value })}
              placeholder="Eigene Reparaturart…"
              className="flex-1 h-10 bg-[#0A0A0B] border-white/10 text-white text-sm"
              data-testid={`${testid}-type-custom`}
              autoFocus
            />
            <button
              type="button"
              onClick={() => { setCustomMode(false); onChange({ repair_type: repairTypes[0] || "" }); }}
              className="w-9 h-10 flex items-center justify-center rounded-md bg-white/5 hover:bg-white/10 text-zinc-400 border border-white/10 text-lg"
              title="Zurück zur Liste"
              data-testid={`${testid}-type-list`}
            >
              ↺
            </button>
          </div>
        ) : (
          <div className="col-span-5 flex gap-1">
            <select
              value={row.repair_type}
              onChange={(e) => {
                if (e.target.value === "__custom__") { setCustomMode(true); onChange({ repair_type: "" }); }
                else onChange({ repair_type: e.target.value });
              }}
              className="flex-1 h-10 bg-[#0A0A0B] border border-white/10 text-white text-sm rounded-md px-2"
              data-testid={`${testid}-type`}
            >
              {[row.repair_type, ...repairTypes.filter((t) => t !== row.repair_type && t !== "")].filter(Boolean).map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
              <option value="__custom__">+ Eigene Reparaturart…</option>
            </select>
          </div>
        )}

        {/* Price */}
        <div className="col-span-3 relative">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500 text-sm pointer-events-none">€</span>
          <Input
            type="number"
            value={row.price}
            disabled={row.on_request}
            onChange={(e) => onChange({ price: e.target.value, on_request: false })}
            placeholder={row.on_request ? "auf Anfrage" : "z.B. 249"}
            className="pl-6 h-10 bg-[#0A0A0B] border-white/10 text-white text-sm numeric disabled:opacity-50 disabled:placeholder:text-amber-500/60"
            data-testid={`${testid}-price`}
          />
        </div>

        {/* On request toggle */}
        <label className="col-span-2 flex items-center gap-1.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={row.on_request}
            onChange={(e) => onChange({ on_request: e.target.checked, price: e.target.checked ? "" : row.price })}
            className="accent-[#DC1928] w-3.5 h-3.5"
            data-testid={`${testid}-on-request`}
          />
          <span className="text-[11px] text-zinc-400">Anfrage</span>
        </label>

        {/* Active toggle */}
        <label className="col-span-1 flex items-center gap-1 cursor-pointer select-none justify-center">
          <input
            type="checkbox"
            checked={row.active}
            onChange={(e) => onChange({ active: e.target.checked })}
            className="accent-emerald-500 w-3.5 h-3.5"
            data-testid={`${testid}-active`}
            title="Aktiv"
          />
        </label>

        {/* Remove */}
        <button
          type="button"
          onClick={onRemove}
          className="col-span-1 w-8 h-8 flex items-center justify-center rounded-md hover:bg-red-500/20 text-red-400 ml-auto"
          data-testid={`${testid}-remove`}
          title="Reparaturart löschen"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Duration + Description in expandable line */}
      <div className="mt-2 grid grid-cols-12 gap-2">
        <Input
          value={row.duration}
          onChange={(e) => onChange({ duration: e.target.value })}
          placeholder="Dauer (z. B. 24–72h)"
          className="col-span-4 h-8 bg-[#0A0A0B] border-white/10 text-white text-xs"
          data-testid={`${testid}-duration`}
        />
        <Input
          value={row.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Kurze Beschreibung (optional)"
          className="col-span-8 h-8 bg-[#0A0A0B] border-white/10 text-white text-xs"
          data-testid={`${testid}-description`}
        />
      </div>
    </div>
  );
}

function NewModelDialog({ category, groups, onClose, onCreated }) {
  const [form, setForm] = useState({ group: groups[0] || "", name: "", year: new Date().getFullYear(), order: 100, active: true, image_url: "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Bitte Modellnamen eingeben");
    setSaving(true);
    try {
      await api.post("/admin/repair-catalog/models", {
        category,
        group: form.group,
        name: form.name.trim(),
        year: Number(form.year) || 0,
        image_url: form.image_url,
        order: Number(form.order) || 0,
        active: !!form.active,
      });
      toast.success("Modell angelegt · Standard-Reparaturarten sind bereits vorbereitet");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#141417] border-white/10 text-white sm:max-w-md" data-testid="new-model-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Neues Modell</DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">
            {'Nach dem Speichern werden automatisch alle Standard-Reparaturarten mit „Preis auf Anfrage" angelegt.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Serie</Label>
            <select
              value={form.group}
              onChange={(e) => setForm({ ...form, group: e.target.value })}
              className="mt-1.5 w-full h-11 bg-[#0A0A0B] border border-white/10 text-white rounded-md px-3"
              data-testid="new-model-group"
            >
              {groups.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Modellname</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1.5 bg-[#0A0A0B] border-white/10 h-11"
              placeholder={category === "ipad" ? "z. B. iPad Pro 11″ (2027)" : "z. B. MacBook Pro 14″ M5 (2026)"}
              data-testid="new-model-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Baujahr</Label>
              <Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="mt-1.5 bg-[#0A0A0B] border-white/10 h-11 numeric" data-testid="new-model-year" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Sortierung</Label>
              <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="mt-1.5 bg-[#0A0A0B] border-white/10 h-11 numeric" data-testid="new-model-order" />
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Bild-URL (optional)</Label>
            <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://…" className="mt-1.5 bg-[#0A0A0B] border-white/10 h-11" data-testid="new-model-image" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#DC1928] w-4 h-4" data-testid="new-model-active" />
            <span className="text-sm text-zinc-300">Aktiv (für Kunden sichtbar)</span>
          </label>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} className="bg-transparent border-white/15 text-white hover:bg-white/5 hover:text-white rounded-full" data-testid="new-model-cancel"><X className="w-4 h-4 mr-1" /> Abbrechen</Button>
          <Button onClick={save} disabled={saving} className="bg-[#DC1928] hover:bg-[#B01829] text-white rounded-full" data-testid="new-model-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            Anlegen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
